import {
  CircularProgress,
  Typography,
  Backdrop,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

/**
 * FullScreenLoader Component (Material UI Version)
 * 
 * A full-screen overlay loader that covers the entire viewport
 * with a Material UI loading spinner and optional message.
 * 
 * Props:
 * - isVisible: boolean - Controls visibility of the loader
 * - message: string - Optional loading message (default: "Loading...")
 * - size: string - Spinner size: 'small', 'medium', 'large' (default: 'medium')
 * - backgroundColor: string - Background color (default: semi-transparent dark)
 */

// Styled backdrop with custom styling
const StyledBackdrop = styled(Backdrop)(({ theme, customBackground }) => ({
  zIndex: 9999,
  backgroundColor: customBackground || 'rgba(12, 20, 46, 0.85)',
  backdropFilter: 'blur(3px)',
  color: '#e7ecff',
}));

// Styled container for loader content
const LoaderContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: theme.spacing(2),
}));

// Styled message typography
const LoaderMessage = styled(Typography)(({ theme }) => ({
  color: '#e7ecff',
  fontWeight: 500,
  letterSpacing: '0.5px',
  fontFamily: 'system-ui, Segoe UI, Roboto, sans-serif',
  userSelect: 'none',
}));

// Custom styled circular progress
const StyledCircularProgress = styled(CircularProgress)(({ theme, loaderSize }) => {
  const sizeMap = {
    small: 32,
    medium: 48,
    large: 64
  };

  const size = sizeMap[loaderSize] || sizeMap.medium;

  return {
    color: '#4f7cff',
    '& .MuiCircularProgress-circle': {
      strokeLinecap: 'round',
    },
    width: `${size}px !important`,
    height: `${size}px !important`,
  };
});

const FullScreenLoader = ({
  isVisible = false,
  message = "Loading...",
  size = "medium",
  backgroundColor = "rgba(0, 0, 0, 0.7)"
}) => {
  return (
    <StyledBackdrop
      open={isVisible}
      customBackground={backgroundColor}
    >
      <LoaderContainer>
        <StyledCircularProgress
          loaderSize={size}
          thickness={4}
          disableShrink
        />
        {message && (
          <LoaderMessage variant="body1">
            {message}
          </LoaderMessage>
        )}
      </LoaderContainer>
    </StyledBackdrop>
  );
};

// PropTypes validation
FullScreenLoader.propTypes = {
  isVisible: PropTypes.bool,
  message: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  backgroundColor: PropTypes.string
};

// Default props
FullScreenLoader.defaultProps = {
  isVisible: false,
  message: "Loading...",
  size: "medium",
  backgroundColor: "rgba(0, 0, 0, 0.7)"
};

export default FullScreenLoader;

/**
 * Usage Examples (Material UI Version):
 * 
 * Basic usage:
 * <FullScreenLoader isVisible={loading} />
 * 
 * With custom message:
 * <FullScreenLoader isVisible={loading} message="Uploading file..." />
 * 
 * With custom size:
 * <FullScreenLoader isVisible={loading} size="large" message="Processing data..." />
 * 
 * With custom background:
 * <FullScreenLoader 
 *   isVisible={loading} 
 *   backgroundColor="rgba(0, 0, 0, 0.7)"
 *   message="Please wait..."
 * />
 * 
 * Features:
 * - Uses Material UI Backdrop for overlay
 * - CircularProgress with custom styling and sizes
 * - Typography component for consistent text styling
 * - Smooth animations with Material UI transitions
 * - Responsive design with theme spacing
 * - Accessibility features built-in
 */
